import React, { useState } from 'react';
import { Building2, Loader2, MessageCircle } from 'lucide-react';
import CopyButton from '../dietitian/CopyButton';
import { btnPrimary, btnSecondary, card, errorText, hint, input, label, sectionTitle } from '../dietitian/ui';
import { makeT, useLanguage } from '../../hooks/useLanguage';
import { CompanyOnboarding } from '../../services/dietitianApi';
import { whatsAppShareUrl } from '../../utils/dietitianInvite';

const strings = {
  english: {
    title: 'New company',
    subtitle: 'Creates the company and a signup invite for its manager. Clients can join before the manager signs up.',
    name: 'Company name',
    email: 'Manager email',
    create: 'Create company',
    creating: 'Creating…',
    nameError: 'Enter a company name.',
    emailError: 'Enter a valid email.',
    error: 'Could not create the company: {msg}',
    done: '{company} created. Send this signup link to {email}:',
    expires: 'The link works once and expires in 14 days.',
    copy: 'Copy link',
    copied: 'Copied',
    whatsapp: 'Send on WhatsApp',
    another: 'Create another',
    message: 'You are invited to manage {company} on BetterChoice. Sign up here: {link}',
  },
  hebrew: {
    title: 'חברה חדשה',
    subtitle: 'יוצר את החברה והזמנת הרשמה למנהל שלה. לקוחות יכולים להצטרף עוד לפני שהמנהל נרשם.',
    name: 'שם החברה',
    email: 'אימייל של המנהל',
    create: 'יצירת חברה',
    creating: 'יוצר…',
    nameError: 'הזינו שם חברה.',
    emailError: 'הזינו אימייל תקין.',
    error: 'יצירת החברה נכשלה: {msg}',
    done: '{company} נוצרה. שלחו את קישור ההרשמה ל-{email}:',
    expires: 'הקישור עובד פעם אחת ותקף ל-14 יום.',
    copy: 'העתקת קישור',
    copied: 'הועתק',
    whatsapp: 'שליחה בוואטסאפ',
    another: 'יצירת חברה נוספת',
    message: 'הוזמנת לנהל את {company} ב-BetterChoice. להרשמה: {link}',
  },
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** sys_admin: company + placeholder manager + manager invite, in one step. */
export default function NewCompanyWithManager({ onCreated }) {
  const { language } = useLanguage();
  const t = makeT(strings, language);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    const nextErrors = {};
    if (!name.trim()) nextErrors.name = t('nameError');
    if (!EMAIL_RE.test(email.trim())) nextErrors.email = t('emailError');
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;
    setBusy(true);
    try {
      const data = await CompanyOnboarding.createWithManager({ name: name.trim(), managerEmail: email.trim().toLowerCase() });
      setResult(data);
      if (onCreated) onCreated(data);
    } catch (err) {
      setErrors({ form: t('error', { msg: err.message || '' }) });
    } finally {
      setBusy(false);
    }
  };

  const reset = () => { setName(''); setEmail(''); setErrors({}); setResult(null); };

  const link = result?.invite?.signup_link;
  const companyName = result?.company?.name || name;

  return (
    <section className={`${card} p-4 sm:p-6`} aria-labelledby="new-company-title">
      <h2 id="new-company-title" className={`${sectionTitle} flex items-center gap-2`}>
        <Building2 size={18} className="text-forest-ondark" aria-hidden />
        {t('title')}
      </h2>
      <p className="mt-1 text-[13px] text-ink-muted">{t('subtitle')}</p>

      {result ? (
        <div className="mt-4 space-y-3" role="status">
          <p className="text-[13px] text-ink">{t('done', { company: companyName, email: result.invite?.email || email })}</p>
          {link ? (
            <>
              <p dir="ltr" className="break-all rounded-md border border-line bg-canvas p-3 font-mono text-[13px] text-ink">{link}</p>
              <p className={hint}>{t('expires')}</p>
              <div className="flex flex-wrap gap-2">
                <CopyButton text={link} label={t('copy')} copiedLabel={t('copied')} />
                <a className={btnPrimary} target="_blank" rel="noopener noreferrer"
                  href={whatsAppShareUrl(t('message', { company: companyName, link }))}>
                  <MessageCircle size={16} aria-hidden />
                  {t('whatsapp')}
                </a>
                <button type="button" className={btnSecondary} onClick={reset}>{t('another')}</button>
              </div>
            </>
          ) : null}
        </div>
      ) : (
        <form onSubmit={submit} noValidate className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-[1fr_1fr_auto] md:items-start">
          <div>
            <label htmlFor="nc-name" className={label}>{t('name')}</label>
            <input id="nc-name" className={input} value={name} maxLength={120}
              aria-invalid={errors.name ? 'true' : undefined}
              onChange={(e) => { setName(e.target.value); setErrors((x) => ({ ...x, name: undefined, form: undefined })); }} />
            {errors.name ? <p className={`${errorText} mt-1`}>{errors.name}</p> : null}
          </div>
          <div>
            <label htmlFor="nc-email" className={label}>{t('email')}</label>
            <input id="nc-email" type="email" dir="ltr" className={input} value={email}
              aria-invalid={errors.email ? 'true' : undefined}
              onChange={(e) => { setEmail(e.target.value); setErrors((x) => ({ ...x, email: undefined, form: undefined })); }} />
            {errors.email ? <p className={`${errorText} mt-1`}>{errors.email}</p> : null}
          </div>
          <div className="md:pt-[26px]">
            <button type="submit" className={`${btnPrimary} w-full md:w-auto`} disabled={busy}>
              {busy ? <Loader2 size={16} className="animate-spin motion-reduce:animate-none" aria-hidden /> : <Building2 size={16} aria-hidden />}
              {busy ? t('creating') : t('create')}
            </button>
          </div>
          {errors.form ? <p className={`${errorText} md:col-span-3`} role="alert">{errors.form}</p> : null}
        </form>
      )}
    </section>
  );
}
